import { OPERATIONAL_FEATURE_INPUTS } from './operational-feature-definitions.js';
export const FEATURE_LOCALES = ['de', 'en', 'tr', 'fr', 'es'];
export const FEATURE_CATEGORIES = [
    'system',
    'sales',
    'catalogue',
    'operations',
    'finance',
    'communication',
    'product-development',
    'manufacturing',
    'analytics',
];
/**
 * Stable persisted identifiers. Renaming or removing a key requires an explicit
 * data migration; display labels must never be used as storage identifiers.
 */
export const FEATURE_KEYS = [
    'auth_sessions',
    'tenant_isolation',
    'identity_access',
    'company_settings',
    'security_two_factor',
    'audit_gobd',
    'system_errors_monitoring',
    'backup_restore',
    'maintenance_mode',
    'feature_management',
    'health_checks',
    'help',
    'privacy_security',
    'dashboard_insights',
    'crm',
    'native_visits',
    'sales_assistant',
    'sales_ai_summaries',
    'suppliers',
    'products_catalogue',
    'inventory_batches',
    'order_processing',
    'document_customer_headers',
    'packaging_summary',
    'pdf_designer',
    'dms',
    'procurement',
    'accounting_datev',
    'intrastat',
    'bank_reconciliation',
    'open_banking_sync',
    'automatic_bank_posting',
    'dunning',
    'automatic_dunning_proposals',
    'customer_portal',
    'customer_portal_documents',
    'customer_portal_orders',
    'newsletter',
    'newsletter_tracking',
    'telephony_ringover',
    'telephone_transcription',
    'ai_call_summaries',
    'whatsapp',
    'questionnaires',
    'custom_fields',
    'automatic_translation',
    'product_specifications',
    'raw_materials',
    'recipes',
    'process_steps',
    'batch_scaling',
    'declarations',
    'nutrition_analysis',
    'label_management',
    'change_control',
    'product_development_dashboard',
    'manufacturing',
    'mrp',
    'batch_traceability',
    'analytics',
    'employee_detail_analytics',
    'feedback',
    'product_news',
    'vat_validation',
    'address_resolution',
    'import_center',
    'notifications',
    'global_search',
    'complaints_returns',
    'scanner_picking',
    'full_inventory',
    'delivery_tours',
    'reusable_containers',
    'electronic_invoices',
    'chatgpt_mcp',
];
function text(de, en, tr, fr, es) {
    return Object.freeze({ de, en, tr, fr, es });
}
const STANDARD_RESOURCE = text('Nutzt nur bei Aufrufen Rechenleistung und Speicher.', 'Uses compute and storage only when invoked.', 'Yalnızca çağrıldığında işlem gücü ve depolama kullanır.', 'Utilise le calcul et le stockage uniquement lors des appels.', 'Usa cálculo y almacenamiento solo cuando se invoca.');
const EXTERNAL_RESOURCE = text('Kann externe Dienste, Kontingente oder kostenpflichtige APIs verwenden.', 'May use external services, quotas, or paid APIs.', 'Harici hizmetleri, kotaları veya ücretli API’leri kullanabilir.', 'Peut utiliser des services externes, des quotas ou des API payantes.', 'Puede usar servicios externos, cuotas o API de pago.');
const RETAIN_ALL = text('Deaktivierung löscht keine Daten; Historie und Aufbewahrungsnachweise bleiben erhalten.', 'Disabling deletes no data; history and retention evidence remain intact.', 'Devre dışı bırakma veri silmez; geçmiş ve saklama kanıtları korunur.', 'La désactivation ne supprime aucune donnée ; l’historique et les preuves sont conservés.', 'La desactivación no elimina datos; se conservan el historial y las pruebas.');
const DISABLE_SAFE = text('Neue Arbeit wird gesperrt; laufende Vorgänge werden sicher abgeschlossen oder pausiert.', 'New work is denied; running work is safely completed or paused.', 'Yeni işler engellenir; çalışan işler güvenle tamamlanır veya duraklatılır.', 'Les nouveaux travaux sont refusés ; les travaux en cours sont terminés ou suspendus.', 'Se bloque el trabajo nuevo; el trabajo en curso finaliza o se pausa de forma segura.');
const REACTIVATE_SAFE = text('Konfiguration und Abhängigkeiten werden erneut geprüft; pausierte Arbeit läuft idempotent weiter.', 'Configuration and dependencies are rechecked; paused work resumes idempotently.', 'Yapılandırma ve bağımlılıklar yeniden denetlenir; duraklatılan işler yinelenmeden sürer.', 'La configuration et les dépendances sont revérifiées ; le travail reprend sans doublon.', 'Se revisan configuración y dependencias; el trabajo pausado continúa sin duplicados.');
const ALWAYS_ON = text('Systemkern; kann nicht deaktiviert werden.', 'System core; cannot be disabled.', 'Sistem çekirdeğidir; devre dışı bırakılamaz.', 'Noyau système ; ne peut pas être désactivé.', 'Núcleo del sistema; no se puede desactivar.');
function defineFeature(input) {
    const systemCore = input.systemCore ?? false;
    return Object.freeze({
        key: input.key,
        name: input.name,
        description: input.description,
        category: input.category,
        icon: input.icon,
        defaultEnabled: systemCore || (input.defaultEnabled ?? true),
        dependencies: Object.freeze([...(input.dependencies ?? [])]),
        requiredPermissions: Object.freeze([...(input.requiredPermissions ?? [])]),
        backendModules: Object.freeze([...(input.backendModules ?? [])]),
        frontendRoutes: Object.freeze([...(input.frontendRoutes ?? [])]),
        configurationRoutes: Object.freeze([...(input.configurationRoutes ?? [])]),
        navigationEntries: Object.freeze([...(input.navigationEntries ?? [])]),
        workers: Object.freeze([...(input.workers ?? [])]),
        schedulers: Object.freeze([...(input.schedulers ?? [])]),
        publicEndpoints: Object.freeze([...(input.publicEndpoints ?? [])]),
        webhooks: Object.freeze([...(input.webhooks ?? [])]),
        requiredConfiguration: Object.freeze([...(input.requiredConfiguration ?? [])]),
        resourceHint: input.resourceHint ?? STANDARD_RESOURCE,
        retentionBehavior: input.retentionBehavior ?? RETAIN_ALL,
        disableStrategy: systemCore ? ALWAYS_ON : (input.disableStrategy ?? DISABLE_SAFE),
        reactivateStrategy: systemCore ? ALWAYS_ON : (input.reactivateStrategy ?? REACTIVATE_SAFE),
        shutdownMode: systemCore ? 'always-on' : (input.shutdownMode ?? 'read-only-history'),
        systemCore,
        platform: Object.freeze({
            available: input.platform?.available ?? true,
            eligiblePlans: Object.freeze([...(input.platform?.eligiblePlans ?? ['all'])]),
            meteredResource: input.platform?.meteredResource ?? null,
        }),
    });
}
const rawCatalogue = [
    defineFeature({
        key: 'auth_sessions',
        name: text('Anmeldung & Sitzungen', 'Sign-in & sessions', 'Oturum açma ve oturumlar', 'Connexion et sessions', 'Inicio de sesión y sesiones'),
        description: text('Sichere Anmeldung, Token-Erneuerung und Sitzungsverwaltung.', 'Secure sign-in, token refresh, and session management.', 'Güvenli oturum açma, belirteç yenileme ve oturum yönetimi.', 'Connexion sécurisée, renouvellement des jetons et gestion des sessions.', 'Inicio seguro, renovación de tokens y gestión de sesiones.'),
        category: 'system',
        icon: 'LogIn',
        systemCore: true,
        backendModules: ['auth'],
        frontendRoutes: ['/login'],
        publicEndpoints: ['/api/v1/auth/*'],
    }),
    defineFeature({
        key: 'tenant_isolation',
        name: text('Mandantentrennung', 'Tenant isolation', 'Kiracı yalıtımı', 'Isolation des tenants', 'Aislamiento de tenant'),
        description: text('Erzwingt die sichere Trennung aller Mandantendaten.', 'Enforces secure separation of all tenant data.', 'Tüm kiracı verilerinin güvenli ayrımını zorunlu kılar.', 'Impose la séparation sécurisée de toutes les données locataires.', 'Impone la separación segura de todos los datos de tenant.'),
        category: 'system',
        icon: 'Layers',
        systemCore: true,
        dependencies: ['auth_sessions'],
        backendModules: ['auth', 'settings'],
    }),
    defineFeature({
        key: 'identity_access',
        name: text('Benutzer, Rollen & Rechte', 'Users, roles & permissions', 'Kullanıcılar, roller ve izinler', 'Utilisateurs, rôles et droits', 'Usuarios, roles y permisos'),
        description: text('Verwaltet Identitäten und rollenbasierte Zugriffsrechte.', 'Manages identities and role-based access.', 'Kimlikleri ve rol tabanlı erişimi yönetir.', 'Gère les identités et les accès par rôle.', 'Gestiona identidades y acceso por roles.'),
        category: 'system',
        icon: 'UsersRound',
        systemCore: true,
        dependencies: ['tenant_isolation'],
        requiredPermissions: ['user:read'],
        backendModules: ['users', 'list-views'],
        frontendRoutes: ['/settings/users', '/settings/roles', '/settings/profile'],
        navigationEntries: ['/settings/users', '/settings/roles'],
    }),
    defineFeature({
        key: 'company_settings',
        name: text('Unternehmensgrunddaten', 'Company master data', 'Şirket ana verileri', 'Données de base société', 'Datos maestros de empresa'),
        description: text('Unternehmen, Nummernkreise, Zahlungs- und Lieferbedingungen.', 'Company, number ranges, payment and delivery terms.', 'Şirket, numara serileri, ödeme ve teslimat koşulları.', 'Société, séquences, conditions de paiement et de livraison.', 'Empresa, series, condiciones de pago y entrega.'),
        category: 'system',
        icon: 'Building2',
        systemCore: true,
        dependencies: ['tenant_isolation'],
        requiredPermissions: ['settings:read'],
        backendModules: ['settings', 'setup', 'introduction'],
        frontendRoutes: [
            '/settings',
            '/settings/price-groups',
            '/settings/contact-responsibilities',
            '/settings/address-types',
            '/settings/company',
            '/settings/setup',
            '/settings/introduction',
        ],
        navigationEntries: ['/settings', '/settings/company'],
    }),
    defineFeature({
        key: 'security_two_factor',
        name: text('Sicherheit & 2FA', 'Security & 2FA', 'Güvenlik ve 2FA', 'Sécurité et 2FA', 'Seguridad y 2FA'),
        description: text('Schützt Konten mit Sicherheitsprotokoll, TOTP und E-Mail-OTP.', 'Protects accounts with security logs, TOTP, and email OTP.', 'Hesapları güvenlik günlüğü, TOTP ve e-posta OTP ile korur.', 'Protège les comptes par journal, TOTP et OTP e-mail.', 'Protege las cuentas con registro, TOTP y OTP por correo.'),
        category: 'system',
        icon: 'ShieldCheck',
        systemCore: true,
        dependencies: ['auth_sessions', 'identity_access'],
        backendModules: ['auth', 'users'],
        frontendRoutes: ['/settings/security', '/settings/security-log'],
        navigationEntries: ['/settings/security'],
    }),
    defineFeature({
        key: 'audit_gobd',
        name: text('Audit & GoBD-Beweiskette', 'Audit & GoBD evidence', 'Denetim ve GoBD kanıt zinciri', 'Audit et chaîne de preuve GoBD', 'Auditoría y cadena de evidencia GoBD'),
        description: text('Bewahrt unveränderbare fachliche und administrative Nachweise.', 'Preserves immutable business and administrative evidence.', 'Değiştirilemez iş ve yönetim kanıtlarını korur.', 'Conserve les preuves métier et administratives immuables.', 'Conserva pruebas empresariales y administrativas inmutables.'),
        category: 'system',
        icon: 'ShieldAlert',
        systemCore: true,
        dependencies: ['tenant_isolation', 'identity_access'],
        backendModules: ['admin', 'legacy-documents'],
        frontendRoutes: ['/admin/audit-vault', '/legacy-documents'],
        navigationEntries: ['/admin/audit-vault', '/legacy-documents'],
    }),
    defineFeature({
        key: 'system_errors_monitoring',
        name: text('Systemfehler & Monitoring', 'System errors & monitoring', 'Sistem hataları ve izleme', 'Erreurs système et supervision', 'Errores del sistema y monitorización'),
        description: text('Erfasst Fehler, Betriebsdiagnosen und Alarmzustände.', 'Captures errors, operational diagnostics, and alert states.', 'Hataları, işletim tanılarını ve alarm durumlarını kaydeder.', 'Collecte erreurs, diagnostics opérationnels et alertes.', 'Registra errores, diagnósticos operativos y alertas.'),
        category: 'system',
        icon: 'Activity',
        systemCore: true,
        dependencies: ['audit_gobd'],
        backendModules: ['admin', 'operations'],
        frontendRoutes: ['/admin/system', '/admin/error-logs', '/settings/operations-alerts'],
        navigationEntries: ['/admin/system', '/admin/error-logs', '/settings/operations-alerts'],
        schedulers: ['operations-monitoring'],
        publicEndpoints: ['/health/uptime'],
    }),
    defineFeature({
        key: 'backup_restore',
        name: text('Backup & Wiederherstellung', 'Backup & restore', 'Yedekleme ve geri yükleme', 'Sauvegarde et restauration', 'Copia y restauración'),
        description: text('Erstellt, verifiziert und stellt verschlüsselte Sicherungen wieder her.', 'Creates, verifies, and restores encrypted backups.', 'Şifreli yedekleri oluşturur, doğrular ve geri yükler.', 'Crée, vérifie et restaure des sauvegardes chiffrées.', 'Crea, verifica y restaura copias cifradas.'),
        category: 'system',
        icon: 'DatabaseBackup',
        systemCore: true,
        dependencies: ['audit_gobd'],
        requiredPermissions: ['backup:view'],
        backendModules: ['admin'],
        frontendRoutes: ['/admin/backups', '/admin/mail-backups'],
        navigationEntries: ['/admin/backups'],
        workers: ['backup', 'backup-restore'],
        schedulers: ['backup-schedule'],
        requiredConfiguration: ['BACKUP_ENCRYPTION_KEY', 'BACKUP_DIR'],
        resourceHint: EXTERNAL_RESOURCE,
    }),
    defineFeature({
        key: 'maintenance_mode',
        name: text('Wartungsmodus', 'Maintenance mode', 'Bakım modu', 'Mode maintenance', 'Modo de mantenimiento'),
        description: text('Sperrt normale Zugriffe während kontrollierter Wartung.', 'Blocks normal access during controlled maintenance.', 'Kontrollü bakım sırasında normal erişimi engeller.', 'Bloque les accès normaux pendant une maintenance contrôlée.', 'Bloquea el acceso normal durante mantenimiento controlado.'),
        category: 'system',
        icon: 'Construction',
        systemCore: true,
        dependencies: ['system_errors_monitoring'],
        backendModules: ['admin'],
        frontendRoutes: ['/admin/system'],
    }),
    defineFeature({
        key: 'feature_management',
        name: text('Funktionen & Module', 'Features & modules', 'Özellikler ve modüller', 'Fonctions et modules', 'Funciones y módulos'),
        description: text('Verwaltet die mandantenbezogene Funktionsverfügbarkeit.', 'Manages tenant-specific feature availability.', 'Kiracıya özgü özellik kullanılabilirliğini yönetir.', 'Gère la disponibilité des fonctions par tenant.', 'Gestiona la disponibilidad de funciones por tenant.'),
        category: 'system',
        icon: 'Boxes',
        systemCore: true,
        dependencies: ['tenant_isolation', 'identity_access', 'audit_gobd'],
        requiredPermissions: ['settings:read'],
        backendModules: ['admin', 'settings'],
        frontendRoutes: ['/admin/features'],
        navigationEntries: ['/admin/features'],
    }),
    defineFeature({
        key: 'health_checks',
        name: text('Healthchecks', 'Health checks', 'Sağlık kontrolleri', 'Contrôles de santé', 'Comprobaciones de salud'),
        description: text('Stellt sichere Start-, Live- und Readiness-Signale bereit.', 'Provides safe startup, liveness, and readiness signals.', 'Güvenli başlangıç, canlılık ve hazır olma sinyalleri sağlar.', 'Fournit des signaux sûrs de démarrage, vie et disponibilité.', 'Proporciona señales seguras de inicio, vida y disponibilidad.'),
        category: 'system',
        icon: 'HeartPulse',
        systemCore: true,
        backendModules: ['health', 'operations'],
        publicEndpoints: ['/health', '/health/ready', '/health/uptime'],
    }),
    defineFeature({
        key: 'help',
        name: text('Hilfe', 'Help', 'Yardım', 'Aide', 'Ayuda'),
        description: text('Bietet kontextsensitive, mehrsprachige Bedienhilfe.', 'Provides contextual, multilingual user help.', 'Bağlama duyarlı çok dilli kullanıcı yardımı sunar.', 'Fournit une aide contextuelle multilingue.', 'Ofrece ayuda contextual multilingüe.'),
        category: 'system',
        icon: 'CircleHelp',
        systemCore: true,
        frontendRoutes: ['/help'],
        publicEndpoints: ['/help/*'],
    }),
    defineFeature({
        key: 'privacy_security',
        name: text('Datenschutz & Basissicherheit', 'Privacy & baseline security', 'Gizlilik ve temel güvenlik', 'Confidentialité et sécurité de base', 'Privacidad y seguridad básica'),
        description: text('Erzwingt Datenschutz-, Cookie-, Lösch- und Sicherheitsgrundlagen.', 'Enforces privacy, cookie, erasure, and baseline security controls.', 'Gizlilik, çerez, silme ve temel güvenlik denetimlerini uygular.', 'Impose les contrôles de confidentialité, cookies, effacement et sécurité.', 'Impone controles de privacidad, cookies, borrado y seguridad.'),
        category: 'system',
        icon: 'LockKeyhole',
        systemCore: true,
        dependencies: ['auth_sessions', 'tenant_isolation', 'audit_gobd'],
        backendModules: ['auth', 'settings', 'admin'],
        schedulers: ['newsletter-evidence-retention', 'questionnaire-retention'],
    }),
    defineFeature({
        key: 'dashboard_insights',
        name: text('Dashboard & Einblicke', 'Dashboard & insights', 'Gösterge paneli ve içgörüler', 'Tableau de bord et aperçus', 'Panel e información'),
        description: text('Verdichtet operative Kennzahlen und anstehende Aufgaben.', 'Summarizes operational metrics and upcoming work.', 'Operasyonel ölçümleri ve yaklaşan işleri özetler.', 'Synthétise les indicateurs et les travaux à venir.', 'Resume métricas operativas y trabajo pendiente.'),
        category: 'analytics',
        icon: 'LayoutDashboard',
        backendModules: ['analytics'],
        frontendRoutes: ['/'],
        navigationEntries: ['/'],
    }),
    defineFeature({
        key: 'crm',
        name: text('CRM, Kunden & Interessenten', 'CRM, customers & prospects', 'CRM, müşteriler ve adaylar', 'CRM, clients et prospects', 'CRM, clientes y prospectos'),
        description: text('Verwaltet Geschäftspartner, Kontakte, Interaktionen und Risiken.', 'Manages business partners, contacts, interactions, and risks.', 'İş ortaklarını, kişileri, etkileşimleri ve riskleri yönetir.', 'Gère partenaires, contacts, interactions et risques.', 'Gestiona socios, contactos, interacciones y riesgos.'),
        category: 'sales',
        icon: 'Contact',
        requiredPermissions: ['customer:read'],
        backendModules: ['customers', 'partners'],
        workers: ['partner-geocoding'],
        frontendRoutes: [
            '/crm/recent-actions',
            '/partners/map',
            '/partners',
            '/partners/customers',
            '/partners/prospects',
            '/partners/:id',
        ],
        navigationEntries: ['/partners/customers'],
    }),
    defineFeature({
        key: 'sales_assistant',
        name: text('Vertriebsassistent', 'Sales assistant', 'Satış asistanı', 'Assistant commercial', 'Asistente de ventas'),
        description: text('Priorisiert Vertriebsaufgaben, Kontakte und Empfehlungen.', 'Prioritizes sales tasks, contacts, and recommendations.', 'Satış görevlerini, kişileri ve önerileri önceliklendirir.', 'Priorise les tâches, contacts et recommandations commerciales.', 'Prioriza tareas, contactos y recomendaciones comerciales.'),
        category: 'sales',
        icon: 'BriefcaseBusiness',
        dependencies: ['crm', 'order_processing'],
        requiredPermissions: ['sales_assistant:use'],
        backendModules: ['sales-assistant'],
        frontendRoutes: ['/sales-assistant', '/sales-assistant/analytics', '/settings/sales-rules'],
        navigationEntries: ['/sales-assistant'],
        workers: ['sales-recommendation'],
        schedulers: ['sales-recommendation'],
    }),
    defineFeature({
        key: 'sales_ai_summaries',
        name: text('KI-Vertriebszusammenfassungen', 'AI sales summaries', 'Yapay zekâ satış özetleri', 'Synthèses commerciales IA', 'Resúmenes de ventas con IA'),
        description: text('Erzeugt lokale KI-Zusammenfassungen für Vertriebsaktivitäten.', 'Creates local AI summaries for sales activity.', 'Satış etkinlikleri için yerel yapay zekâ özetleri oluşturur.', 'Crée des synthèses IA locales des activités commerciales.', 'Crea resúmenes locales con IA de la actividad comercial.'),
        category: 'sales',
        icon: 'Sparkles',
        defaultEnabled: false,
        dependencies: ['sales_assistant'],
        requiredPermissions: ['sales_ai_summaries:create'],
        backendModules: ['sales-assistant'],
        requiredConfiguration: ['OLLAMA_ENABLED', 'OLLAMA_API_URL', 'OLLAMA_MODEL'],
        resourceHint: EXTERNAL_RESOURCE,
        platform: { meteredResource: 'ollama-tokens' },
    }),
    defineFeature({
        key: 'suppliers',
        name: text('Lieferanten', 'Suppliers', 'Tedarikçiler', 'Fournisseurs', 'Proveedores'),
        description: text('Verwaltet Lieferantenstammdaten und lieferantenbezogene Referenzen.', 'Manages supplier master data and supplier references.', 'Tedarikçi ana verilerini ve referanslarını yönetir.', 'Gère les données de base et références fournisseurs.', 'Gestiona datos maestros y referencias de proveedores.'),
        category: 'sales',
        icon: 'Truck',
        dependencies: ['crm'],
        requiredPermissions: ['customer:read'],
        backendModules: ['partners'],
        workers: ['partner-geocoding'],
        frontendRoutes: ['/partners/map', '/partners/suppliers', '/partners/:id'],
        navigationEntries: ['/partners/suppliers'],
    }),
    defineFeature({
        key: 'products_catalogue',
        name: text('Artikel & Katalog', 'Products & catalogue', 'Ürünler ve katalog', 'Articles et catalogue', 'Artículos y catálogo'),
        description: text('Verwaltet Artikel, Kategorien, Preise und Produktbilder.', 'Manages products, categories, prices, and product images.', 'Ürünleri, kategorileri, fiyatları ve görselleri yönetir.', 'Gère articles, catégories, prix et images produit.', 'Gestiona artículos, categorías, precios e imágenes.'),
        category: 'catalogue',
        icon: 'Package',
        requiredPermissions: ['product:read'],
        backendModules: ['products', 'product-categories', 'price-calculations'],
        frontendRoutes: ['/products/pricing', '/products', '/products/:id'],
        navigationEntries: ['/products', '/products/pricing'],
    }),
    defineFeature({
        key: 'inventory_batches',
        name: text('Lager, Bestände & Chargen', 'Inventory, stock & batches', 'Depo, stok ve partiler', 'Stock, inventaire et lots', 'Almacén, existencias y lotes'),
        description: text('Verwaltet Bestände, Lagerbewegungen und Chargen.', 'Manages stock, inventory movements, and batches.', 'Stokları, depo hareketlerini ve partileri yönetir.', 'Gère stocks, mouvements et lots.', 'Gestiona existencias, movimientos y lotes.'),
        category: 'operations',
        icon: 'Warehouse',
        dependencies: ['products_catalogue'],
        requiredPermissions: ['inventory:read'],
        backendModules: ['products', 'warehouse-imports'],
        frontendRoutes: [
            '/inventory/overview',
            '/inventory/products/:id',
            '/inventory/batches/:id',
            '/inventory/import',
        ],
        navigationEntries: ['/products', '/inventory/overview', '/inventory/import'],
    }),
    defineFeature({
        key: 'order_processing',
        name: text('Auftragsabwicklung & Belege', 'Order processing & documents', 'Sipariş işleme ve belgeler', 'Traitement des commandes et documents', 'Gestión de pedidos y documentos'),
        description: text('Erstellt Angebote, Aufträge, Lieferscheine, Rechnungen und Gutschriften.', 'Creates quotes, orders, delivery notes, invoices, and credit notes.', 'Teklif, sipariş, irsaliye, fatura ve alacak dekontları oluşturur.', 'Crée devis, commandes, bons de livraison, factures et avoirs.', 'Crea ofertas, pedidos, albaranes, facturas y abonos.'),
        category: 'sales',
        icon: 'FileText',
        dependencies: ['crm', 'products_catalogue'],
        requiredPermissions: ['document:read'],
        backendModules: ['documents'],
        frontendRoutes: [
            '/documents',
            '/documents/new',
            '/documents/:id',
            '/sales/*',
            '/finance/open-items',
        ],
        navigationEntries: ['/sales/all'],
    }),
    defineFeature({
        key: 'packaging_summary',
        name: text('Gebinde und Gewichtsangaben', 'Packaging and weights', 'Ambalaj ve ağırlıklar', 'Conditionnements et poids', 'Embalajes y pesos'),
        description: text('Optionale rechnerische und bestätigte Angaben in Belegen.', 'Optional calculated and confirmed document totals.', 'Belgelerde isteğe bağlı hesaplanan ve onaylanan toplamlar.', 'Totaux calculés et confirmés facultatifs dans les documents.', 'Totales calculados y confirmados opcionales en documentos.'),
        category: 'sales',
        icon: 'Package',
        dependencies: ['order_processing'],
        requiredPermissions: ['document:read'],
        backendModules: ['documents', 'settings'],
        frontendRoutes: ['/settings/packaging-weights'],
        configurationRoutes: ['/settings/packaging-weights'],
        navigationEntries: ['/settings/packaging-weights'],
    }),
    defineFeature({
        key: 'document_customer_headers',
        name: text('Beleg-Zusatzfelder', 'Document customer headers', 'Belge müşteri başlıkları', 'En-têtes clients des documents', 'Cabeceras de cliente en documentos'),
        description: text('Pflegt versionierte kundenbezogene Zusatzwerte und Layouts in Belegen.', 'Maintains versioned customer-specific values and layouts in documents.', 'Belgelerde sürümlü müşteriye özgü değerleri ve düzenleri yönetir.', 'Gère les valeurs et mises en page client versionnées des documents.', 'Gestiona valores y diseños de cliente versionados en documentos.'),
        category: 'sales',
        icon: 'PanelTop',
        dependencies: ['order_processing', 'custom_fields'],
        requiredPermissions: ['document_customer_header:read'],
        backendModules: ['documents'],
        frontendRoutes: ['/settings/document-headers'],
        navigationEntries: ['/settings/document-headers'],
    }),
    defineFeature({
        key: 'pdf_designer',
        name: text('PDF-Designer', 'PDF designer', 'PDF tasarımcısı', 'Concepteur PDF', 'Diseñador PDF'),
        description: text('Gestaltet und rendert versionierte Dokument- und Etikettenvorlagen.', 'Designs and renders versioned document and label templates.', 'Sürümlü belge ve etiket şablonlarını tasarlar ve işler.', 'Conçoit et génère des modèles versionnés de documents et étiquettes.', 'Diseña y genera plantillas versionadas de documentos y etiquetas.'),
        category: 'operations',
        icon: 'FileType2',
        dependencies: ['order_processing'],
        requiredPermissions: ['settings:manage_templates'],
        backendModules: ['pdf-templates'],
        frontendRoutes: ['/settings/pdf-designer', '/settings/pdf-designer/:id'],
        navigationEntries: ['/settings/pdf-designer'],
        workers: ['product-specification-pdf'],
        schedulers: ['product-specification-pdf-recovery'],
    }),
    defineFeature({
        key: 'dms',
        name: text('Dokumentenmanagement', 'Document management', 'Belge yönetimi', 'Gestion documentaire', 'Gestión documental'),
        description: text('Archiviert Dateien nachvollziehbar mit Metadaten und Versionen.', 'Archives files with traceable metadata and versions.', 'Dosyaları izlenebilir meta veriler ve sürümlerle arşivler.', 'Archive les fichiers avec métadonnées et versions traçables.', 'Archiva archivos con metadatos y versiones trazables.'),
        category: 'operations',
        icon: 'FolderArchive',
        requiredPermissions: ['dms:read'],
        backendModules: ['dms'],
        frontendRoutes: ['/dms'],
        navigationEntries: ['/dms'],
        requiredConfiguration: ['DMS_STORAGE_DIR'],
        resourceHint: text('Benötigt dauerhaftes, gesichertes Dateisystemvolumen.', 'Requires durable, backed-up file storage.', 'Kalıcı ve yedeklenen dosya depolaması gerektirir.', 'Nécessite un stockage de fichiers durable et sauvegardé.', 'Requiere almacenamiento duradero y respaldado.'),
    }),
    defineFeature({
        key: 'procurement',
        name: text('Einkauf', 'Procurement', 'Satın alma', 'Achats', 'Compras'),
        description: text('Steuert Bestellungen, Wareneingänge und Rechnungsabgleich.', 'Controls purchase orders, goods receipts, and invoice matching.', 'Satın alma siparişlerini, mal kabullerini ve fatura eşleştirmeyi yönetir.', 'Pilote commandes, réceptions et rapprochement des factures.', 'Gestiona pedidos, recepciones y conciliación de facturas.'),
        category: 'operations',
        icon: 'ShoppingCart',
        dependencies: ['suppliers', 'products_catalogue', 'inventory_batches'],
        requiredPermissions: ['purchase:read'],
        backendModules: ['procurement'],
        frontendRoutes: ['/procurement/*'],
        navigationEntries: ['/procurement/orders'],
        requiredConfiguration: ['PROCUREMENT_MEDIA_DIR'],
    }),
    defineFeature({
        key: 'accounting_datev',
        name: text('Finanzwesen & DATEV', 'Finance & DATEV', 'Finans ve DATEV', 'Finance et DATEV', 'Finanzas y DATEV'),
        description: text('Unterstützt Buchungsgrundlagen, Steuern und DATEV-Export.', 'Supports accounting data, taxes, and DATEV export.', 'Muhasebe verilerini, vergileri ve DATEV dışa aktarımını destekler.', 'Prend en charge comptabilité, taxes et export DATEV.', 'Admite contabilidad, impuestos y exportación DATEV.'),
        category: 'finance',
        icon: 'Calculator',
        dependencies: ['order_processing'],
        requiredPermissions: ['export:datev'],
        backendModules: ['accounting'],
        frontendRoutes: ['/accounting/settings', '/accounting/datev'],
        navigationEntries: ['/accounting/datev'],
    }),
    defineFeature({
        key: 'intrastat',
        name: text('Intrastat', 'Intrastat', 'Intrastat', 'Intrastat', 'Intrastat'),
        description: text('Bereitet innergemeinschaftliche Warenmeldungen prüfbar auf.', 'Prepares auditable intra-EU goods declarations.', 'AB içi mal bildirimlerini denetlenebilir biçimde hazırlar.', 'Prépare les déclarations de biens intra-UE auditables.', 'Prepara declaraciones auditables de bienes intracomunitarios.'),
        category: 'finance',
        icon: 'Truck',
        dependencies: ['accounting_datev', 'order_processing'],
        requiredPermissions: ['export:intrastat'],
        backendModules: ['accounting'],
        frontendRoutes: ['/accounting/intrastat'],
        navigationEntries: ['/accounting/intrastat'],
    }),
    defineFeature({
        key: 'bank_reconciliation',
        name: text('Bankabgleich', 'Bank reconciliation', 'Banka mutabakatı', 'Rapprochement bancaire', 'Conciliación bancaria'),
        description: text('Ordnet Bankbewegungen offenen Belegen und Zahlungen zu.', 'Matches bank movements to open documents and payments.', 'Banka hareketlerini açık belgelere ve ödemelere eşler.', 'Rapproche les mouvements bancaires des documents et paiements ouverts.', 'Concilia movimientos bancarios con documentos y pagos abiertos.'),
        category: 'finance',
        icon: 'Landmark',
        dependencies: ['accounting_datev', 'order_processing'],
        requiredPermissions: ['banking:read'],
        backendModules: ['banking'],
        frontendRoutes: [
            '/finance/bank-accounts',
            '/finance/bank-transactions',
            '/finance/bank-reconciliation',
            '/finance/payments',
            '/settings/banking',
        ],
        navigationEntries: ['/finance/bank-reconciliation'],
    }),
    defineFeature({
        key: 'open_banking_sync',
        name: text('Open-Banking-Synchronisation', 'Open banking synchronization', 'Açık bankacılık eşitlemesi', 'Synchronisation bancaire ouverte', 'Sincronización de banca abierta'),
        description: text('Ruft Konten und Umsätze über konfigurierte Banking-Anbieter ab.', 'Retrieves accounts and transactions from configured banking providers.', 'Yapılandırılmış sağlayıcılardan hesap ve işlemleri alır.', 'Récupère comptes et opérations auprès des fournisseurs configurés.', 'Obtiene cuentas y movimientos de proveedores configurados.'),
        category: 'finance',
        icon: 'RefreshCw',
        defaultEnabled: false,
        dependencies: ['bank_reconciliation'],
        requiredPermissions: ['banking:sync'],
        backendModules: ['banking'],
        workers: ['banking-sync'],
        schedulers: ['banking-sync'],
        configurationRoutes: ['/settings/banking'],
        publicEndpoints: ['/api/v1/banking/callback/gocardless'],
        webhooks: ['/api/v1/banking/webhooks/:endpointId'],
        requiredConfiguration: [
            'OPEN_BANKING_ENABLED',
            'BANKING_ENCRYPTION_KEY',
            'BANKING_CALLBACK_URL',
            'tenant:open_banking_provider',
        ],
        resourceHint: EXTERNAL_RESOURCE,
        shutdownMode: 'deny-and-pause-ingress',
    }),
    defineFeature({
        key: 'automatic_bank_posting',
        name: text('Automatische Bankverbuchung', 'Automatic bank posting', 'Otomatik banka kaydı', 'Comptabilisation bancaire automatique', 'Contabilización bancaria automática'),
        description: text('Bucht eindeutig zugeordnete Bankbewegungen nach Freigaberegeln.', 'Posts unambiguously matched bank transactions under approval rules.', 'Kesin eşleşen banka işlemlerini onay kurallarına göre kaydeder.', 'Comptabilise les opérations clairement rapprochées selon les règles.', 'Contabiliza movimientos inequívocos según reglas de aprobación.'),
        category: 'finance',
        icon: 'BadgeCheck',
        defaultEnabled: false,
        dependencies: ['open_banking_sync'],
        requiredPermissions: ['banking:auto_post'],
        backendModules: ['banking'],
    }),
    defineFeature({
        key: 'dunning',
        name: text('Mahnwesen', 'Dunning', 'Alacak takibi', 'Relances', 'Reclamación de pagos'),
        description: text('Verwaltet Mahnläufe, Freigaben, Schreiben und Zahlungen.', 'Manages dunning runs, approvals, notices, and payments.', 'Alacak takip çalıştırmalarını, onayları, bildirimleri ve ödemeleri yönetir.', 'Gère campagnes de relance, validations, courriers et paiements.', 'Gestiona procesos, aprobaciones, avisos y pagos.'),
        category: 'finance',
        icon: 'MessageSquareWarning',
        dependencies: ['order_processing'],
        requiredPermissions: ['dunning:read'],
        backendModules: ['dunning'],
        frontendRoutes: ['/finance/dunning'],
        navigationEntries: ['/finance/dunning'],
        workers: ['dunning'],
    }),
    defineFeature({
        key: 'automatic_dunning_proposals',
        name: text('Automatische Mahnvorschläge', 'Automatic dunning proposals', 'Otomatik takip önerileri', 'Propositions automatiques de relance', 'Propuestas automáticas de reclamación'),
        description: text('Ermittelt fällige Posten planmäßig ohne sie automatisch zu versenden.', 'Schedules due-item proposals without sending them automatically.', 'Vadesi gelen kalem önerilerini otomatik göndermeden planlar.', 'Planifie les propositions échues sans les envoyer automatiquement.', 'Programa propuestas vencidas sin enviarlas automáticamente.'),
        category: 'finance',
        icon: 'Clock3',
        dependencies: ['dunning'],
        requiredPermissions: ['dunning:prepare'],
        backendModules: ['dunning'],
        workers: ['dunning'],
        schedulers: ['dunning'],
        shutdownMode: 'deny-new-work-and-finish-running',
    }),
    defineFeature({
        key: 'customer_portal',
        name: text('Kundenportal', 'Customer portal', 'Müşteri portalı', 'Portail client', 'Portal de clientes'),
        description: text('Bietet Kunden einen getrennten, sicheren Self-Service-Zugang.', 'Provides customers with isolated, secure self-service access.', 'Müşterilere yalıtılmış güvenli self servis erişimi sağlar.', 'Offre aux clients un accès libre-service isolé et sécurisé.', 'Ofrece acceso autoservicio seguro y aislado a clientes.'),
        category: 'sales',
        icon: 'PanelsTopLeft',
        dependencies: ['crm'],
        requiredPermissions: ['portal_access:manage'],
        backendModules: ['customer-portal'],
        frontendRoutes: [
            '/portal',
            '/portal/login',
            '/portal/accept',
            '/portal/reset',
            '/portal/account',
            '/portal/team',
        ],
        publicEndpoints: ['/api/v1/portal/auth/*', '/api/v1/portal/invitations/*'],
    }),
    defineFeature({
        key: 'customer_portal_documents',
        name: text('Kundenportal-Dokumente', 'Customer portal documents', 'Müşteri portalı belgeleri', 'Documents du portail client', 'Documentos del portal de clientes'),
        description: text('Stellt freigegebene Belege, Mahnungen und DMS-Dateien lesend bereit.', 'Provides read-only approved documents, dunning notices, and DMS files.', 'Onaylı belgeleri, takip bildirimlerini ve DMS dosyalarını salt okunur sunar.', 'Fournit en lecture seule documents, relances et fichiers DMS approuvés.', 'Ofrece documentos, avisos y archivos DMS aprobados en solo lectura.'),
        category: 'sales',
        icon: 'Files',
        dependencies: ['customer_portal', 'order_processing', 'dms'],
        backendModules: ['customer-portal'],
        frontendRoutes: ['/portal/documents', '/portal/dunning', '/portal/files'],
        navigationEntries: ['/portal/documents', '/portal/files'],
        publicEndpoints: [
            '/api/v1/portal/documents/*',
            '/api/v1/portal/dunning/*',
            '/api/v1/portal/files/*',
        ],
    }),
    defineFeature({
        key: 'customer_portal_orders',
        name: text('Kundenportal-Bestellungen', 'Customer portal ordering', 'Müşteri portalı siparişleri', 'Commandes du portail client', 'Pedidos del portal de clientes'),
        description: text('Ermöglicht Katalog, Warenkorb und kontrollierte Bestellanfragen.', 'Enables catalogue, cart, and controlled order requests.', 'Katalog, sepet ve kontrollü sipariş taleplerini etkinleştirir.', 'Active catalogue, panier et demandes de commande contrôlées.', 'Habilita catálogo, carrito y solicitudes de pedido controladas.'),
        category: 'sales',
        icon: 'ShoppingBasket',
        dependencies: ['customer_portal', 'products_catalogue', 'order_processing'],
        backendModules: ['customer-portal'],
        frontendRoutes: ['/portal/catalog', '/portal/cart'],
        navigationEntries: ['/portal/catalog', '/portal/cart'],
        publicEndpoints: [
            '/api/v1/portal/catalog/*',
            '/api/v1/portal/cart/*',
            '/api/v1/portal/order-templates/*',
            '/api/v1/portal/checkout',
        ],
    }),
    defineFeature({
        key: 'newsletter',
        name: text('Newsletter', 'Newsletter', 'Bülten', 'Newsletter', 'Boletín'),
        description: text('Verwaltet Einwilligungen, Zielgruppen, Kampagnen und Zustellung.', 'Manages consent, audiences, campaigns, and delivery.', 'Onayları, hedef kitleleri, kampanyaları ve teslimatı yönetir.', 'Gère consentements, audiences, campagnes et envois.', 'Gestiona consentimientos, audiencias, campañas y entregas.'),
        category: 'communication',
        icon: 'Megaphone',
        defaultEnabled: false,
        dependencies: ['crm'],
        requiredPermissions: ['newsletter:read'],
        backendModules: ['newsletter'],
        frontendRoutes: [
            '/marketing/*',
            '/newsletter/subscribe',
            '/newsletter/confirm',
            '/newsletter/preferences',
        ],
        navigationEntries: ['/marketing'],
        workers: ['newsletter-delivery'],
        schedulers: ['newsletter-delivery-recovery', 'newsletter-domain-verification'],
        publicEndpoints: ['/api/v1/public/newsletter/*'],
        webhooks: ['/api/v1/public/newsletter/provider-webhooks/:providerId'],
        requiredConfiguration: [
            'NEWSLETTER_ENABLED',
            'MAIL_FEATURES_ENABLED',
            'SMTP_HOST',
            'SMTP_FROM_ADDRESS',
            'NEWSLETTER_SUPPRESSION_HMAC_SECRET',
        ],
        resourceHint: EXTERNAL_RESOURCE,
        shutdownMode: 'deny-and-pause-ingress',
    }),
    defineFeature({
        key: 'newsletter_tracking',
        name: text('Newsletter-Tracking', 'Newsletter tracking', 'Bülten takibi', 'Suivi des newsletters', 'Seguimiento de boletines'),
        description: text('Erfasst datenschutzkonforme Öffnungs- und Klickereignisse.', 'Captures privacy-compliant open and click events.', 'Gizliliğe uygun açılma ve tıklama olaylarını kaydeder.', 'Collecte les ouvertures et clics conformes à la confidentialité.', 'Registra aperturas y clics respetando la privacidad.'),
        category: 'communication',
        icon: 'MousePointerClick',
        defaultEnabled: false,
        dependencies: ['newsletter'],
        backendModules: ['newsletter'],
        publicEndpoints: [
            '/api/v1/public/newsletter/track/open/:token',
            '/api/v1/public/newsletter/track/click/:token',
        ],
        shutdownMode: 'deny-and-pause-ingress',
    }),
    defineFeature({
        key: 'telephony_ringover',
        name: text('Telefonie & Ringover', 'Telephony & Ringover', 'Telefon ve Ringover', 'Téléphonie et Ringover', 'Telefonía y Ringover'),
        description: text('Verknüpft Anrufe, Gesprächsprotokolle und CRM-Kontakte.', 'Links calls, call records, and CRM contacts.', 'Aramaları, görüşme kayıtlarını ve CRM kişilerini bağlar.', 'Relie appels, comptes rendus et contacts CRM.', 'Vincula llamadas, registros y contactos CRM.'),
        category: 'communication',
        icon: 'PhoneCall',
        defaultEnabled: false,
        dependencies: ['crm'],
        requiredPermissions: ['telephony:use'],
        backendModules: ['telephony'],
        frontendRoutes: ['/admin/telephony-ai'],
        configurationRoutes: ['/admin/telephony-ai'],
        navigationEntries: ['/admin/telephony-ai'],
        webhooks: ['/api/v1/telephony/webhook'],
        requiredConfiguration: ['tenant:telephony_ringover_connection'],
        resourceHint: EXTERNAL_RESOURCE,
        shutdownMode: 'deny-and-pause-ingress',
    }),
    defineFeature({
        key: 'telephone_transcription',
        name: text('Telefontranskription', 'Call transcription', 'Telefon dökümü', 'Transcription des appels', 'Transcripción de llamadas'),
        description: text('Transkribiert gespeicherte Gesprächsaufnahmen über Whisper.', 'Transcribes stored call recordings through Whisper.', 'Kaydedilmiş görüşmeleri Whisper ile metne dönüştürür.', 'Transcrit les appels enregistrés via Whisper.', 'Transcribe llamadas grabadas mediante Whisper.'),
        category: 'communication',
        icon: 'AudioLines',
        defaultEnabled: false,
        dependencies: ['telephony_ringover'],
        backendModules: ['telephony'],
        workers: ['telephony-audio-processing'],
        schedulers: ['telephony-processing-recovery'],
        configurationRoutes: ['/admin/telephony-ai'],
        requiredConfiguration: ['WHISPER_ENABLED', 'WHISPER_API_URL', 'tenant:whisper_configuration'],
        resourceHint: EXTERNAL_RESOURCE,
        platform: { meteredResource: 'whisper-seconds' },
    }),
    defineFeature({
        key: 'ai_call_summaries',
        name: text('KI-Gesprächszusammenfassung', 'AI call summaries', 'Yapay zekâ görüşme özetleri', 'Résumés d’appels IA', 'Resúmenes de llamadas con IA'),
        description: text('Erstellt aus Transkripten lokale, prüfbare Gesprächszusammenfassungen.', 'Creates local, reviewable call summaries from transcripts.', 'Dökümlerden yerel ve incelenebilir görüşme özetleri oluşturur.', 'Crée des résumés locaux et vérifiables à partir des transcriptions.', 'Crea resúmenes locales y revisables a partir de transcripciones.'),
        category: 'communication',
        icon: 'Sparkles',
        defaultEnabled: false,
        dependencies: ['telephone_transcription'],
        requiredPermissions: ['telephony:view_ai_summary'],
        backendModules: ['telephony'],
        workers: ['telephony-ai-summary'],
        configurationRoutes: ['/admin/telephony-ai'],
        requiredConfiguration: [
            'OLLAMA_ENABLED',
            'OLLAMA_API_URL',
            'OLLAMA_MODEL',
            'tenant:ollama_configuration',
        ],
        resourceHint: EXTERNAL_RESOURCE,
        platform: { meteredResource: 'ollama-tokens' },
    }),
    defineFeature({
        key: 'whatsapp',
        name: text('WhatsApp', 'WhatsApp', 'WhatsApp', 'WhatsApp', 'WhatsApp'),
        description: text('Verarbeitet zugeordnete Geschäftsnachrichten über die WhatsApp API.', 'Processes assigned business messages through the WhatsApp API.', 'Atanmış iş mesajlarını WhatsApp API üzerinden işler.', 'Traite les messages professionnels attribués via l’API WhatsApp.', 'Procesa mensajes empresariales asignados mediante la API de WhatsApp.'),
        category: 'communication',
        icon: 'MessageCircle',
        defaultEnabled: false,
        dependencies: ['crm'],
        requiredPermissions: ['whatsapp:read'],
        backendModules: ['whatsapp'],
        frontendRoutes: ['/whatsapp', '/admin/whatsapp'],
        configurationRoutes: ['/admin/whatsapp'],
        navigationEntries: ['/whatsapp'],
        webhooks: ['/api/v1/whatsapp/webhook'],
        requiredConfiguration: ['tenant:whatsapp_business_connection', 'WHATSAPP_MEDIA_DIR'],
        resourceHint: EXTERNAL_RESOURCE,
        shutdownMode: 'deny-and-pause-ingress',
    }),
    defineFeature({
        key: 'questionnaires',
        name: text('Fragebögen', 'Questionnaires', 'Anketler', 'Questionnaires', 'Cuestionarios'),
        description: text('Erstellt Vorlagen, mobile Läufe, Kundenübergaben und Exporte.', 'Creates templates, mobile runs, customer handoffs, and exports.', 'Şablonlar, mobil çalışmalar, müşteri devirleri ve dışa aktarımlar oluşturur.', 'Crée modèles, sessions mobiles, remises client et exports.', 'Crea plantillas, ejecuciones móviles, entregas al cliente y exportaciones.'),
        category: 'operations',
        icon: 'ClipboardCheck',
        requiredPermissions: ['questionnaire:use'],
        backendModules: ['questionnaires'],
        frontendRoutes: [
            '/field',
            '/field/runs/*',
            '/field/runs/:id/customer',
            '/admin/questionnaires/*',
            '/admin/questionnaire-results/*',
        ],
        navigationEntries: ['/field'],
        workers: ['questionnaire-export'],
        schedulers: ['questionnaire-export-recovery'],
        publicEndpoints: ['/field/runs/:id/customer'],
    }),
    defineFeature({
        key: 'custom_fields',
        name: text('Zusatzfelder', 'Custom fields', 'Özel alanlar', 'Champs personnalisés', 'Campos personalizados'),
        description: text('Erweitert fachliche Datensätze mit versionierten, typisierten Feldern.', 'Extends business records with versioned, typed fields.', 'İş kayıtlarını sürümlü ve türlendirilmiş alanlarla genişletir.', 'Étend les dossiers métier par des champs typés et versionnés.', 'Amplía registros empresariales con campos tipados y versionados.'),
        category: 'operations',
        icon: 'ListPlus',
        requiredPermissions: ['custom_fields:read'],
        backendModules: ['custom-fields'],
        frontendRoutes: ['/admin/custom-fields'],
        navigationEntries: ['/admin/custom-fields'],
    }),
    defineFeature({
        key: 'automatic_translation',
        name: text('Automatische Übersetzung', 'Automatic translation', 'Otomatik çeviri', 'Traduction automatique', 'Traducción automática'),
        description: text('Übersetzt freigegebene Inhalte lokal mit Glossar und Review.', 'Translates approved content locally with glossary and review.', 'Onaylı içerikleri sözlük ve inceleme ile yerel olarak çevirir.', 'Traduit localement les contenus approuvés avec glossaire et révision.', 'Traduce localmente contenido aprobado con glosario y revisión.'),
        category: 'operations',
        icon: 'Languages',
        defaultEnabled: false,
        requiredPermissions: ['translations:generate'],
        backendModules: ['translations'],
        frontendRoutes: ['/settings/translations'],
        navigationEntries: ['/settings/translations'],
        workers: ['translation-batch'],
        schedulers: ['translation-batch-recovery'],
        requiredConfiguration: ['OLLAMA_ENABLED', 'OLLAMA_API_URL', 'OLLAMA_MODEL'],
        resourceHint: EXTERNAL_RESOURCE,
        platform: { meteredResource: 'ollama-tokens' },
    }),
    defineFeature({
        key: 'product_specifications',
        name: text('Produkt-Spezifikationen', 'Product specifications', 'Ürün spesifikasyonları', 'Spécifications produit', 'Especificaciones de producto'),
        description: text('Versioniert, prüft und veröffentlicht technische Produktspezifikationen.', 'Versions, reviews, and publishes technical product specifications.', 'Teknik ürün spesifikasyonlarını sürümler, inceler ve yayımlar.', 'Versionne, révise et publie les spécifications techniques.', 'Versiona, revisa y publica especificaciones técnicas.'),
        category: 'product-development',
        icon: 'FileCheck2',
        dependencies: ['products_catalogue'],
        requiredPermissions: ['product_specification:read'],
        backendModules: ['product-specifications'],
        frontendRoutes: [
            '/admin/product-specifications',
            '/products/:productId/specifications/:specificationId/revisions/:revisionId',
        ],
        navigationEntries: ['/admin/product-specifications'],
        workers: ['product-specification-pdf'],
        schedulers: ['product-specification-pdf-recovery'],
    }),
    defineFeature({
        key: 'raw_materials',
        name: text('Rohstoffe & Materialien', 'Raw materials', 'Hammaddeler ve malzemeler', 'Matières premières', 'Materias primas'),
        description: text('Pflegt Materialstämme, Freigaben und produktbezogene Stoffdaten.', 'Maintains material masters, approvals, and product material data.', 'Malzeme ana verilerini, onayları ve ürün madde verilerini yönetir.', 'Gère référentiels matière, validations et données produit.', 'Gestiona maestros, aprobaciones y datos materiales del producto.'),
        category: 'product-development',
        icon: 'Boxes',
        dependencies: ['products_catalogue'],
        requiredPermissions: ['raw_material:read'],
        backendModules: ['plm-materials'],
    }),
    defineFeature({
        key: 'recipes',
        name: text('Rezepturen', 'Recipes', 'Reçeteler', 'Recettes', 'Recetas'),
        description: text('Versioniert Rezepturen, Stücklisten, Kosten und Freigaben.', 'Versions recipes, bills of material, costs, and approvals.', 'Reçeteleri, ürün ağaçlarını, maliyetleri ve onayları sürümler.', 'Versionne recettes, nomenclatures, coûts et validations.', 'Versiona recetas, listas de materiales, costes y aprobaciones.'),
        category: 'product-development',
        icon: 'FlaskConical',
        dependencies: ['products_catalogue', 'raw_materials'],
        requiredPermissions: ['recipe:read'],
        backendModules: ['recipes'],
        frontendRoutes: ['/product-development/recipes', '/product-development/recipes/:id'],
        navigationEntries: ['/product-development/recipes'],
    }),
    defineFeature({
        key: 'process_steps',
        name: text('Prozessschritte', 'Process steps', 'Proses adımları', 'Étapes de processus', 'Pasos de proceso'),
        description: text('Definiert Arbeitsfolgen, Ressourcen und Prozessparameter.', 'Defines routings, resources, and process parameters.', 'İş akışlarını, kaynakları ve proses parametrelerini tanımlar.', 'Définit gammes, ressources et paramètres de processus.', 'Define rutas, recursos y parámetros de proceso.'),
        category: 'product-development',
        icon: 'Workflow',
        dependencies: ['products_catalogue'],
        requiredPermissions: ['product_development:read'],
        backendModules: ['process-routings'],
    }),
    defineFeature({
        key: 'batch_scaling',
        name: text('Batch Scaling', 'Batch scaling', 'Parti ölçekleme', 'Mise à l’échelle des lots', 'Escalado de lotes'),
        description: text('Skaliert freigegebene Rezepturen auf Zielmengen und Anlagen.', 'Scales approved recipes to target quantities and equipment.', 'Onaylı reçeteleri hedef miktarlara ve ekipmana ölçekler.', 'Adapte les recettes validées aux quantités et équipements cibles.', 'Escala recetas aprobadas a cantidades y equipos objetivo.'),
        category: 'product-development',
        icon: 'Scale',
        dependencies: ['recipes', 'process_steps'],
        requiredPermissions: ['recipe:read'],
        backendModules: ['batch-scaling'],
        frontendRoutes: ['/product-development/batch-scaling'],
        navigationEntries: ['/product-development/batch-scaling'],
    }),
    defineFeature({
        key: 'declarations',
        name: text('Deklarationen', 'Declarations', 'Beyanlar', 'Déclarations', 'Declaraciones'),
        description: text('Erstellt versionierte Zutaten- und Kennzeichnungsdeklarationen.', 'Creates versioned ingredient and labelling declarations.', 'Sürümlü içerik ve etiketleme beyanları oluşturur.', 'Crée des déclarations versionnées d’ingrédients et d’étiquetage.', 'Crea declaraciones versionadas de ingredientes y etiquetado.'),
        category: 'product-development',
        icon: 'ScrollText',
        dependencies: ['recipes'],
        requiredPermissions: ['declaration:read'],
        backendModules: ['declarations'],
        frontendRoutes: [
            '/product-development/declarations',
            '/product-development/declarations/:id',
            '/product-development/declaration-catalog',
        ],
        navigationEntries: ['/product-development/declarations'],
    }),
    defineFeature({
        key: 'nutrition_analysis',
        name: text('Nährwert- & Analyseverwaltung', 'Nutrition & analysis management', 'Besin ve analiz yönetimi', 'Gestion nutritionnelle et analyses', 'Gestión nutricional y de análisis'),
        description: text('Berechnet und versioniert Analyseprofile, Parameter und Nährwerte.', 'Calculates and versions analysis profiles, parameters, and nutrition values.', 'Analiz profillerini, parametreleri ve besin değerlerini hesaplar ve sürümler.', 'Calcule et versionne profils, paramètres et valeurs nutritionnelles.', 'Calcula y versiona perfiles, parámetros y valores nutricionales.'),
        category: 'product-development',
        icon: 'ChartNoAxesCombined',
        dependencies: ['recipes', 'raw_materials'],
        requiredPermissions: ['analysis:read'],
        backendModules: ['analysis'],
        frontendRoutes: [
            '/product-development/analysis',
            '/product-development/analysis/:id',
            '/product-development/analysis-parameters',
        ],
        navigationEntries: ['/product-development/analysis'],
    }),
    defineFeature({
        key: 'label_management',
        name: text('Label Management', 'Label management', 'Etiket yönetimi', 'Gestion des étiquettes', 'Gestión de etiquetas'),
        description: text('Entwirft, prüft und druckt versionierte Produktetiketten.', 'Designs, reviews, and prints versioned product labels.', 'Sürümlü ürün etiketlerini tasarlar, inceler ve yazdırır.', 'Conçoit, révise et imprime des étiquettes produit versionnées.', 'Diseña, revisa e imprime etiquetas de producto versionadas.'),
        category: 'product-development',
        icon: 'Tags',
        dependencies: ['products_catalogue', 'declarations', 'nutrition_analysis'],
        requiredPermissions: ['label:read'],
        backendModules: ['label-management', 'pdf-templates'],
        frontendRoutes: ['/product-development/labels', '/product-development/labels/:id'],
        navigationEntries: ['/product-development/labels'],
    }),
    defineFeature({
        key: 'change_control',
        name: text('Change Control', 'Change control', 'Değişiklik kontrolü', 'Contrôle des changements', 'Control de cambios'),
        description: text('Plant, prüft und genehmigt nachvollziehbare Produktänderungen.', 'Plans, reviews, and approves traceable product changes.', 'İzlenebilir ürün değişikliklerini planlar, inceler ve onaylar.', 'Planifie, révise et approuve les changements produit traçables.', 'Planifica, revisa y aprueba cambios trazables de producto.'),
        category: 'product-development',
        icon: 'GitCompareArrows',
        dependencies: ['products_catalogue'],
        requiredPermissions: ['change_control:read'],
        backendModules: ['change-control'],
        frontendRoutes: [
            '/product-development/change-control',
            '/product-development/change-control/:id',
        ],
        navigationEntries: ['/product-development/change-control'],
    }),
    defineFeature({
        key: 'product_development_dashboard',
        name: text('Produktentwicklungs-Dashboard', 'Product development dashboard', 'Ürün geliştirme paneli', 'Tableau de bord développement produit', 'Panel de desarrollo de producto'),
        description: text('Bündelt Freigaben, Risiken und Aufgaben der Produktentwicklung.', 'Consolidates approvals, risks, and product-development work.', 'Ürün geliştirme onaylarını, risklerini ve işlerini birleştirir.', 'Regroupe validations, risques et tâches de développement produit.', 'Reúne aprobaciones, riesgos y tareas de desarrollo de producto.'),
        category: 'product-development',
        icon: 'LayoutDashboard',
        dependencies: ['products_catalogue'],
        requiredPermissions: ['product_development:read'],
        backendModules: ['product-development-dashboard'],
        frontendRoutes: ['/product-development'],
        navigationEntries: ['/product-development'],
    }),
    defineFeature({
        key: 'manufacturing',
        name: text('Produktion', 'Manufacturing', 'Üretim', 'Production', 'Producción'),
        description: text('Plant, reserviert, verbraucht und meldet Produktionsaufträge fertig.', 'Plans, reserves, consumes, and completes production orders.', 'Üretim emirlerini planlar, rezerve eder, tüketir ve tamamlar.', 'Planifie, réserve, consomme et termine les ordres de production.', 'Planifica, reserva, consume y completa órdenes de producción.'),
        category: 'manufacturing',
        icon: 'Factory',
        dependencies: ['products_catalogue', 'inventory_batches', 'recipes', 'process_steps'],
        requiredPermissions: ['manufacturing:read'],
        backendModules: ['manufacturing'],
        frontendRoutes: ['/manufacturing/production-orders', '/manufacturing/production-orders/:id'],
        navigationEntries: ['/manufacturing/production-orders'],
    }),
    defineFeature({
        key: 'mrp',
        name: text('Materialbedarfsplanung', 'Material requirements planning', 'Malzeme ihtiyaç planlaması', 'Planification des besoins matières', 'Planificación de necesidades'),
        description: text('Berechnet Bedarfe und überführt Vorschläge kontrolliert in Einkauf oder Produktion.', 'Calculates demand and converts proposals into procurement or production.', 'İhtiyaçları hesaplar ve önerileri satın alma ya da üretime dönüştürür.', 'Calcule les besoins et convertit les propositions en achats ou production.', 'Calcula necesidades y convierte propuestas en compras o producción.'),
        category: 'manufacturing',
        icon: 'Calculator',
        dependencies: ['products_catalogue', 'inventory_batches', 'recipes'],
        requiredPermissions: ['mrp:read'],
        backendModules: ['mrp'],
        frontendRoutes: ['/manufacturing/mrp'],
        navigationEntries: ['/manufacturing/mrp'],
    }),
    defineFeature({
        key: 'batch_traceability',
        name: text('Chargenrückverfolgung', 'Batch traceability', 'Parti izlenebilirliği', 'Traçabilité des lots', 'Trazabilidad de lotes'),
        description: text('Verfolgt Chargen vorwärts und rückwärts und dokumentiert Sperren.', 'Traces batches forward and backward and records blocks.', 'Partileri ileri ve geri izler ve blokajları kaydeder.', 'Trace les lots en amont et aval et consigne les blocages.', 'Rastrea lotes hacia delante y atrás y registra bloqueos.'),
        category: 'manufacturing',
        icon: 'GitBranch',
        dependencies: ['inventory_batches', 'manufacturing'],
        requiredPermissions: ['traceability:read'],
        backendModules: ['traceability'],
        frontendRoutes: ['/manufacturing/traceability'],
        navigationEntries: ['/manufacturing/traceability'],
    }),
    defineFeature({
        key: 'analytics',
        name: text('Analytics', 'Analytics', 'Analitik', 'Analytique', 'Analítica'),
        description: text('Erstellt mandantenbezogene Geschäftsanalysen und Berichte.', 'Creates tenant-scoped business analytics and reports.', 'Kiracı kapsamlı iş analizleri ve raporları oluşturur.', 'Crée des analyses et rapports métier par tenant.', 'Crea análisis e informes empresariales por tenant.'),
        category: 'analytics',
        icon: 'BarChart3',
        requiredPermissions: ['report:read'],
        backendModules: ['analytics'],
        frontendRoutes: ['/analytics'],
        navigationEntries: ['/analytics'],
    }),
    defineFeature({
        key: 'employee_detail_analytics',
        name: text('Mitarbeiterdetailauswertung', 'Employee detail analytics', 'Çalışan detay analizi', 'Analyse détaillée des employés', 'Análisis detallado de empleados'),
        description: text('Zeigt berechtigten Rollen personenbezogene Leistungsdetails.', 'Shows authorized roles person-level performance details.', 'Yetkili rollere kişi düzeyinde performans ayrıntıları gösterir.', 'Affiche aux rôles autorisés les performances individuelles.', 'Muestra a roles autorizados detalles de rendimiento individual.'),
        category: 'analytics',
        icon: 'Users',
        defaultEnabled: false,
        dependencies: ['analytics'],
        requiredPermissions: ['employee_performance:view'],
        backendModules: ['analytics'],
        frontendRoutes: ['/analytics/employee-performance'],
        navigationEntries: ['/analytics/employee-performance'],
    }),
    defineFeature({
        key: 'feedback',
        name: text('Feedback', 'Feedback', 'Geri bildirim', 'Retour', 'Comentarios'),
        description: text('Erfasst Nutzerfeedback mit datensparsamen Diagnosedaten.', 'Captures user feedback with data-minimized diagnostics.', 'Veri minimizasyonlu tanı verileriyle kullanıcı geri bildirimi toplar.', 'Recueille les retours avec des diagnostics minimisés.', 'Recoge comentarios con diagnósticos minimizados.'),
        category: 'system',
        icon: 'MessageSquareWarning',
        backendModules: ['feedback'],
        frontendRoutes: ['/admin/feedback'],
        navigationEntries: ['/admin/feedback'],
        requiredPermissions: ['feedback:manage'],
    }),
    defineFeature({
        key: 'product_news',
        name: text('Neuigkeiten & Changelog', 'News & changelog', 'Haberler ve değişiklik günlüğü', 'Actualités et journal des changements', 'Noticias y registro de cambios'),
        description: text('Zeigt versionierte Produktneuigkeiten und funktionale Änderungen.', 'Shows versioned product news and functional changes.', 'Sürümlü ürün haberlerini ve işlevsel değişiklikleri gösterir.', 'Affiche les actualités versionnées et évolutions fonctionnelles.', 'Muestra noticias versionadas y cambios funcionales.'),
        category: 'system',
        icon: 'Newspaper',
        backendModules: ['product-news'],
        frontendRoutes: ['/news'],
        navigationEntries: ['/news'],
    }),
    defineFeature({
        key: 'vat_validation',
        name: text('Umsatzsteuer-ID-Prüfung', 'VAT ID validation', 'KDV numarası doğrulama', 'Validation du numéro de TVA', 'Validación de NIF-IVA'),
        description: text('Prüft Umsatzsteuer-IDs nachweisbar über den konfigurierten Dienst.', 'Validates VAT IDs with evidence through the configured service.', 'KDV numaralarını yapılandırılmış hizmet üzerinden kanıtlı doğrular.', 'Valide les numéros de TVA avec preuve via le service configuré.', 'Valida NIF-IVA con evidencia mediante el servicio configurado.'),
        category: 'sales',
        icon: 'BadgeCheck',
        dependencies: ['crm'],
        requiredPermissions: ['vat_validation:read'],
        backendModules: ['partners'],
        workers: ['vat-validation'],
        schedulers: ['vat-validation'],
        requiredConfiguration: ['VIES_SERVICE_URL'],
        resourceHint: EXTERNAL_RESOURCE,
    }),
    defineFeature({
        key: 'address_resolution',
        name: text('Adressauflösung', 'Address resolution', 'Adres çözümleme', 'Résolution d’adresse', 'Resolución de direcciones'),
        description: text('Ergänzt und validiert Adressen über konfigurierte Geodienste.', 'Completes and validates addresses through configured geo services.', 'Adresleri yapılandırılmış coğrafi hizmetlerle tamamlar ve doğrular.', 'Complète et valide les adresses via les services géographiques configurés.', 'Completa y valida direcciones mediante servicios geográficos configurados.'),
        category: 'sales',
        icon: 'MapPin',
        dependencies: ['crm'],
        backendModules: ['address-resolution'],
        resourceHint: EXTERNAL_RESOURCE,
    }),
    defineFeature({
        key: 'import_center',
        name: text('Importcenter', 'Import center', 'İçe aktarma merkezi', 'Centre d’import', 'Centro de importación'),
        description: text('Validiert, importiert und bei Bedarf kontrolliert zurückrollt Stamm- und Bewegungsdaten.', 'Validates, imports, and safely rolls back master and transaction data.', 'Ana ve işlem verilerini doğrular, içe aktarır ve güvenle geri alır.', 'Valide, importe et annule de façon contrôlée les données.', 'Valida, importa y revierte de forma controlada datos maestros y transaccionales.'),
        category: 'operations',
        icon: 'Database',
        requiredPermissions: ['imports:read'],
        backendModules: ['admin'],
        frontendRoutes: ['/admin/data-hub'],
        navigationEntries: ['/admin/data-hub'],
        workers: ['data-import', 'lexware-import'],
        schedulers: ['data-import-recovery', 'lexware-import-recovery'],
    }),
    defineFeature({
        key: 'notifications',
        name: text('Benachrichtigungen', 'Notifications', 'Bildirimler', 'Notifications', 'Notificaciones'),
        description: text('Erzeugt, verteilt und verwaltet fachliche In-App-Benachrichtigungen.', 'Creates, distributes, and manages business in-app notifications.', 'İş uygulama içi bildirimlerini oluşturur, dağıtır ve yönetir.', 'Crée, distribue et gère les notifications métier dans l’application.', 'Crea, distribuye y gestiona notificaciones empresariales en la aplicación.'),
        category: 'communication',
        icon: 'Bell',
        backendModules: ['notifications'],
        frontendRoutes: ['/settings/notifications'],
        navigationEntries: ['/settings/notifications'],
        schedulers: ['notification-scheduler'],
    }),
    defineFeature({
        key: 'global_search',
        name: text('Globale Suche', 'Global search', 'Genel arama', 'Recherche globale', 'Búsqueda global'),
        description: text('Durchsucht ausschließlich aktive und berechtigte fachliche Bereiche.', 'Searches only active and authorized business areas.', 'Yalnızca etkin ve yetkili iş alanlarında arama yapar.', 'Recherche uniquement dans les domaines actifs et autorisés.', 'Busca solo en áreas empresariales activas y autorizadas.'),
        category: 'system',
        icon: 'Search',
        requiredPermissions: ['global_search:use'],
        backendModules: ['search'],
        frontendRoutes: ['/search'],
    }),
    defineFeature({
        key: 'native_visits',
        name: text('Native Kundenbesuche', 'Native customer visits', 'Yerel müşteri ziyaretleri', 'Visites clients natives', 'Visitas nativas a clientes'),
        description: text('Geschützte Kundenpräsentation mit separatem Zugriff und Belegentwürfen.', 'Protected customer presentation with separate access and sales drafts.', 'Ayrı erişim ve satış taslaklarıyla korumalı müşteri sunumu.', 'Présentation client protégée avec accès distinct et brouillons commerciaux.', 'Presentación protegida con acceso separado y borradores comerciales.'),
        category: 'sales',
        icon: 'Tablet',
        defaultEnabled: true,
        dependencies: ['auth_sessions', 'tenant_isolation', 'crm', 'products_catalogue'],
        requiredPermissions: ['customer:read', 'product:read'],
        backendModules: ['native-visits'],
        publicEndpoints: ['/api/v1/native-app', '/api/v1/native-presentation/*'],
        requiredConfiguration: ['NATIVE_INSTALLATION_ID'],
        shutdownMode: 'deny-and-pause-ingress',
    }),
    ...OPERATIONAL_FEATURE_INPUTS.map((input) => defineFeature({ ...input, configurationRoutes: ['/settings/operations'] })),
    defineFeature({
        key: 'chatgpt_mcp',
        name: text('ChatGPT-Verbindung', 'ChatGPT connection', 'ChatGPT bağlantısı', 'Connexion ChatGPT', 'Conexión con ChatGPT'),
        description: text('Eigene ChatGPT-Zugriffe freigeben und widerrufen.', 'Approve and revoke your own ChatGPT access.', 'Kendi ChatGPT erişiminizi onaylayın ve iptal edin.', 'Autorisez et révoquez vos propres accès ChatGPT.', 'Autorice y revoque sus propios accesos de ChatGPT.'),
        category: 'communication',
        icon: 'message-circle',
        dependencies: ['auth_sessions', 'identity_access', 'tenant_isolation'],
        backendModules: ['mcp'],
        frontendRoutes: ['/settings/chatgpt', '/settings/chatgpt/authorize'],
        navigationEntries: ['/settings/chatgpt'],
        publicEndpoints: [
            '/api/v1/mcp',
            '/.well-known/oauth-authorization-server',
            '/.well-known/oauth-protected-resource',
            '/.well-known/oauth-protected-resource/api/v1/mcp',
        ],
        shutdownMode: 'deny-and-pause-ingress',
        disableStrategy: text('Neue Anbindungen und alle ChatGPT-Aufrufe werden gesperrt; ERP-Daten bleiben erhalten.', 'New connections and all ChatGPT calls are blocked; ERP data is retained.', 'Yeni bağlantılar ve tüm ChatGPT çağrıları engellenir; ERP verileri korunur.', 'Les nouvelles connexions et tous les appels ChatGPT sont bloqués ; les données ERP sont conservées.', 'Se bloquean nuevas conexiones y todas las llamadas de ChatGPT; se conservan los datos del ERP.'),
        reactivateStrategy: text('Mandantenstatus, Benutzerrechte und bestehende Freigaben werden bei jedem Aufruf erneut geprüft.', 'Company status, user permissions and existing approvals are rechecked on each call.', 'Şirket durumu, kullanıcı izinleri ve mevcut onaylar her çağrıda yeniden denetlenir.', 'L’état de l’entreprise, les droits et les autorisations sont revérifiés à chaque appel.', 'En cada llamada se comprueban el estado de la empresa, los permisos y las autorizaciones existentes.'),
    }),
];
function validateAndBuildCatalogue(source) {
    const keySet = new Set();
    for (const definition of source) {
        if (keySet.has(definition.key))
            throw new Error(`Duplicate feature key: ${definition.key}`);
        keySet.add(definition.key);
    }
    if (keySet.size !== FEATURE_KEYS.length) {
        const missing = FEATURE_KEYS.filter((key) => !keySet.has(key));
        throw new Error(`Feature catalogue is incomplete: ${missing.join(', ')}`);
    }
    const dependents = new Map(FEATURE_KEYS.map((key) => [key, []]));
    for (const definition of source) {
        for (const dependency of definition.dependencies) {
            if (!keySet.has(dependency)) {
                throw new Error(`Unknown dependency ${dependency} on feature ${definition.key}`);
            }
            if (dependency === definition.key) {
                throw new Error(`Feature ${definition.key} cannot depend on itself`);
            }
            dependents.get(dependency)?.push(definition.key);
        }
    }
    const visiting = new Set();
    const visited = new Set();
    const dependenciesByKey = new Map(source.map((definition) => [definition.key, definition.dependencies]));
    const visit = (key) => {
        if (visiting.has(key))
            throw new Error(`Cyclic feature dependency at ${key}`);
        if (visited.has(key))
            return;
        visiting.add(key);
        for (const dependency of dependenciesByKey.get(key) ?? [])
            visit(dependency);
        visiting.delete(key);
        visited.add(key);
    };
    for (const key of FEATURE_KEYS)
        visit(key);
    return Object.freeze(source.map((definition) => Object.freeze({
        ...definition,
        dependents: Object.freeze([...(dependents.get(definition.key) ?? [])]),
    })));
}
/** Complete, validated source of truth shared by API, worker, and web. */
export const FEATURE_CATALOGUE = validateAndBuildCatalogue(rawCatalogue);
export const FEATURE_CATALOGUE_BY_KEY = new Map(FEATURE_CATALOGUE.map((definition) => [definition.key, definition]));
export const SYSTEM_CORE_FEATURE_KEYS = Object.freeze(FEATURE_CATALOGUE.filter((definition) => definition.systemCore).map(({ key }) => key));
export const RECOMMENDED_FEATURE_KEYS = Object.freeze(FEATURE_CATALOGUE.filter((definition) => definition.defaultEnabled).map(({ key }) => key));
export function isFeatureKey(value) {
    return FEATURE_CATALOGUE_BY_KEY.has(value);
}
export function getFeatureDefinition(key) {
    const definition = FEATURE_CATALOGUE_BY_KEY.get(key);
    if (definition === undefined)
        throw new Error(`Unknown feature key: ${key}`);
    return definition;
}
export function localizeFeatureText(value, locale) {
    return value[FEATURE_LOCALES.includes(locale) ? locale : 'en'];
}
//# sourceMappingURL=catalogue.js.map